import React, { useCallback, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Header, Image, Text, Actions, Link, Modal } from '@gasbuddy/react-components';
import { useWindowSize } from '@gasbuddy/react-hooks';
import { addDays } from 'date-fns';
import { localyticsEvent, noop } from '../../../lib/utils';
import settings from '../../../lib/utils/settings';
import performTracking from '../../../lib/utils/performTracking';

const HomeScreenCardPropType = {
  name: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['card', 'toast', 'root']).isRequired,
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  image_url: PropTypes.string.isRequired,
  image_width_dp: PropTypes.number.isRequired,
  image_height_dp: PropTypes.number.isRequired,
  cta_label: PropTypes.string.isRequired,
  cta_url: PropTypes.string.isRequired,
  secondary_cta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }),
  track: PropTypes.arrayOf(
    PropTypes.shape({
      event: PropTypes.oneOf([
        'suppress', // ignore for tracking
        'remove', // ignore tracking
        'unsuppress', // ignore tracking
        'view', // fire when modal is shown
        'cta_click', // fire when primary action executes
        'secondary_cta_click', // fire when secondary action executes
        'dismissed', // fire when modal is dismissed
      ]).isRequired,
      action: PropTypes.oneOf([
        'internal_post', // fire a first party event to UCS
        'localytics', // fire a localytics event
        'url', // fire a GET request using performTracking util
        'webapi', // ignore tracking
      ]).isRequired,
      variant: PropTypes.string, // only gets provided if action === localytics
      values: PropTypes.arrayOf( // send this for localytics or first party tracking
        PropTypes.shape({
          key: PropTypes.string,
          value: PropTypes.string,
        }),
      ),
    }),
  ),
};

function getAnalyticsEventWithContext(event) {
  const { values = [] } = event;
  const context = values.reduce((result, obj) => ({
    ...result,
    [obj.key]: obj.value,
  }), {});

  return {
    ...event,
    values: context,
  };
}

/**
 * returns an object categorizing tracking events into view events, primary cta events and secondary cta events
 * @param {*} events
 */
function getCategorizedTrackingEvents(events = []) {
  const trackableEvents = events
    .filter(t => !['remove', 'unsuppress'].includes(t.event))
    .map(getAnalyticsEventWithContext);

  const primaryCtaEvents = trackableEvents.filter(({ event }) => event === 'cta_click');
  const secondaryCtaEvents = trackableEvents.filter(({ event }) => event === 'secondary_cta_click');
  const viewEvents = trackableEvents.filter(({ event }) => event === 'view');
  const dismissalEvents = trackableEvents.filter(({ event }) => event === 'dismissed');

  return {
    primaryCtaEvents,
    secondaryCtaEvents,
    viewEvents,
    dismissalEvents,
  };
}

/**
 * Filters tracking events into first party tracking events, localytics events and external tracking events
 * @param {*} events Tracking events found in card.track prop
 * @param {*} source name of the card from which tracking is to be triggered
 */
function filterTrackingEvents(events, source) {
  const firstPartyEvents = events.filter(e => e.action === 'internal_post').map(e => ({ ...e, source }));
  const localyticsEvents = events.filter(e => e.action === 'localytics');
  const externalTrackingEvents = events.filter(e => e.action === 'url');

  return {
    firstPartyEvents,
    localyticsEvents,
    externalTrackingEvents,
  };
}

/**
 * Records analytics events using localytics
 * @param {*} events An array of events with each array member having "event" as event name and "context" as data to record for the event
 */
function recordLocalyticsEvents(events) {
  events.forEach((e) => {
    localyticsEvent(e.variant, e.values);
  });
}

function fireAnalyticsEvents(events, source, firstPartyTrackingFn) {
  const {
    firstPartyEvents,
    localyticsEvents,
    externalTrackingEvents,
  } = filterTrackingEvents(events, source);

  firstPartyEvents.forEach((e) => {
    firstPartyTrackingFn(e);
  });

  recordLocalyticsEvents(localyticsEvents);

  performTracking(externalTrackingEvents);
}

function HomeScreenCardModalContent({
  card,
  performFirstPartyTracking,
}) {
  const { mobile: isOnMobileDevice } = useWindowSize();

  const handlePrimaryCtaClicked = useCallback(() => {
    const { primaryCtaEvents } = getCategorizedTrackingEvents(card?.track);
    fireAnalyticsEvents(primaryCtaEvents, card.name, performFirstPartyTracking);
  }, [card, performFirstPartyTracking]);

  const handleSecondaryCtaClicked = useCallback(() => {
    const { secondaryCtaEvents } = getCategorizedTrackingEvents(card?.track);
    fireAnalyticsEvents(secondaryCtaEvents, card.name, performFirstPartyTracking);
  }, [card, performFirstPartyTracking]);


  const {
    title,
    body,
    image_url: imageUrl,
    image_width_dp: imageWidth,
    image_height_dp: imageHeight,
    secondary_cta: secondaryCta,
    cta_url: primaryCtaUrl,
    cta_label: primaryCtaLabel,
  } = card;

  const imageSizeMultiplier = isOnMobileDevice ? 1.5 : 2;

  const finalImageUrl = imageUrl
    .replace('{{widthpx}}', imageWidth * imageSizeMultiplier)
    .replace('{{heightpx}}', imageHeight * imageSizeMultiplier);

  const replaceLineBreaks = text => text.replace(/(\n)/g, '<br />');

  return (
    <React.Fragment>
      <Header
        as="h3"
        color="midnight"
        dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
          __html: replaceLineBreaks(title),
        }}
      />
      <br />
      <Text as="p" centered>
        <Image
          src={finalImageUrl}
          alt={title}
        />
      </Text>
      <br />
      <Text
        as="p"
        dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
          __html: replaceLineBreaks(body),
        }}
      />
      <br />
      <Actions>
        <Actions.Button
          primary
          as="a"
          href={primaryCtaUrl}
          onClick={handlePrimaryCtaClicked}
          fluid
          tablet={secondaryCta ? 6 : 4}
          desktop={secondaryCta ? 6 : 4}
        >
          {primaryCtaLabel}
        </Actions.Button>
        {!!secondaryCta && (
          <Actions.Action tablet={6} desktop={6}>
            <Link
              to={secondaryCta.url}
              onClick={handleSecondaryCtaClicked}
              uppercase
            >
              {secondaryCta.label}
            </Link>
          </Actions.Action>
        )}
      </Actions>
    </React.Fragment>
  );
}

HomeScreenCardModalContent.propTypes = {
  card: PropTypes.shape(HomeScreenCardPropType).isRequired,
  performFirstPartyTracking: PropTypes.func,
};

HomeScreenCardModalContent.defaultProps = {
  performFirstPartyTracking: noop,
};

export default function HomeScreenCardModal({
  cards,
  isFetching,
  error,
  fetchHomeScreenCards,
  nextInterval,
  performFirstPartyTracking,
}) {
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [activeCard, setActiveCard] = useState();

  // We should only show next home screen card modal if the set interval has passed already
  const shouldShowNextModal = useMemo(() => (nextInterval
    ? Date.now() > new Date(nextInterval).valueOf()
    : true
  ), [nextInterval]);

  const handleModalShown = useCallback(() => {
    const { viewEvents } = getCategorizedTrackingEvents(activeCard?.track);
    fireAnalyticsEvents(viewEvents, activeCard.name, performFirstPartyTracking);
  }, [activeCard, performFirstPartyTracking]);

  const handleModalDismissed = useCallback(() => {
    const { dismissalEvents } = getCategorizedTrackingEvents(activeCard?.track);
    fireAnalyticsEvents(dismissalEvents, activeCard.name, performFirstPartyTracking);
    setShouldShowModal(false);
  }, [activeCard, performFirstPartyTracking]);

  useEffect(() => {
    if (shouldShowNextModal && !activeCard) {
      if (!isFetching && !error && !cards) {
        fetchHomeScreenCards();
      } else if (cards?.length) {
        const { shownHomeScreenCards } = settings;

        // Show a card with suppressed: false aka unsuppressed/active and has not been shown yet
        const nextHomeScreenCard = cards.find(c => !c.suppressed && !shownHomeScreenCards.includes(c.name));
        if (nextHomeScreenCard) {
          setActiveCard(nextHomeScreenCard);
          setShouldShowModal(true);
          // Update next home screen card to appear after 2 days
          settings.nextHomeScreenCardInterval = addDays(Date.now(), 2);
          // Update cards that have been shown to user
          settings.shownHomeScreenCards = JSON.stringify([].concat(shownHomeScreenCards || [], nextHomeScreenCard.name));
        }
      }
    }
  }, [shouldShowNextModal, isFetching, error, cards, activeCard, fetchHomeScreenCards]);

  return !!activeCard && (
    <Modal
      content={(
        <HomeScreenCardModalContent
          card={activeCard}
          performFirstPartyTracking={performFirstPartyTracking}
        />
      )}
      size="sm"
      onOpen={handleModalShown}
      onClose={handleModalDismissed}
      forceIsShowing={shouldShowModal}
      bgClickable={false}
    />
  );
}

HomeScreenCardModal.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape(HomeScreenCardPropType),
  ),
  isFetching: PropTypes.bool,
  error: PropTypes.string,
  fetchHomeScreenCards: PropTypes.func,
  nextInterval: PropTypes.string,
  performFirstPartyTracking: PropTypes.func,
};

HomeScreenCardModal.defaultProps = {
  cards: undefined,
  isFetching: false,
  error: undefined,
  fetchHomeScreenCards: noop,
  nextInterval: undefined,
  performFirstPartyTracking: noop,
};
